import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";
import {
  Users,
  CalendarDays,
  TrendingUp,
  RefreshCw,
  LogOut,
  User,
  Phone,
  Mail,
  Clock,
} from "lucide-react";
import { fetchStats, fetchInquiries, clearToken } from "./AdminAuth";

const DAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="border border-white/10 bg-ink/40 p-7 transition-colors duration-300 hover:border-bronze-light/40"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-bronze-light">
        <Icon size={15} />
        <p className="font-sans text-[10px] uppercase tracking-[0.3em]">{label}</p>
      </div>
      <p className="mt-5 font-sans text-5xl font-extralight tracking-wide text-white">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [s, inq] = await Promise.all([fetchStats(), fetchInquiries()]);
      setStats(s);
      setInquiries(inq);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const chartData = (stats?.chart || []).map((d) => {
    const dt = new Date(d.date + "T00:00:00");
    return { label: DAY_LABELS[dt.getDay()], count: d.count };
  });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-deep-brown text-[#F1E6D7]" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1200px] px-6 py-12 md:px-10 md:py-16">
        {/* Header */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="font-sans text-[11px] uppercase tracking-[0.4em] text-bronze-light">Private</p>
            <h1 className="mt-3 font-sans text-4xl font-light uppercase tracking-[0.2em] text-white">
              Dashboard
            </h1>
            <p className="mt-3 font-sans text-[11px] uppercase tracking-[0.25em] text-[#F1E6D7]/40">
              Leads &amp; inquiries overview
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 font-sans text-[10px] uppercase tracking-[0.3em] text-bronze-light transition-all duration-300 hover:border-bronze-light hover:bg-bronze-light hover:text-ink"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 font-sans text-[10px] uppercase tracking-[0.3em] text-[#F1E6D7]/70 transition-all duration-300 hover:border-white/40 hover:text-white"
            >
              <LogOut size={13} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard icon={CalendarDays} label="Leads Today" value={stats?.leads_today ?? "—"} testid="stat-leads-today" />
          <StatCard icon={TrendingUp} label="Leads (7 Days)" value={stats?.leads_week ?? "—"} testid="stat-leads-week" />
          <StatCard icon={Users} label="Total Leads" value={stats?.total_leads ?? "—"} testid="stat-total-leads" />
        </div>

        {/* Chart */}
        <div className="mt-6 border border-white/10 bg-ink/40 p-7">
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-bronze-light">
            Leads · Last 7 Days
          </p>
          <div className="mt-8 h-[220px] w-full">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={1}>
                <BarChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#C7A47E", fontSize: 10, letterSpacing: 2 }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(199,164,126,0.08)" }}
                    contentStyle={{
                      background: "#16100C",
                      border: "1px solid rgba(199,164,126,0.3)",
                      borderRadius: 0,
                      color: "#F1E6D7",
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "#C7A47E" }}
                  />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={90}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill="#8a6a49" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center font-sans text-[11px] uppercase tracking-[0.3em] text-[#F1E6D7]/30">
                {loading ? "Loading…" : "No data"}
              </div>
            )}
          </div>
        </div>

        {/* Inquiries */}
        <div className="mt-14 flex items-end justify-between">
          <h2 className="font-sans text-2xl font-light uppercase tracking-[0.2em] text-white">Inquiries</h2>
          <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#F1E6D7]/40">
            {inquiries.length} total
          </p>
        </div>

        <div className="mt-8 space-y-4" data-testid="admin-inquiries-list">
          {inquiries.length === 0 && !loading && (
            <div className="border border-white/10 bg-ink/40 p-10 text-center font-light text-[#F1E6D7]/50">
              No inquiries yet.
            </div>
          )}
          {inquiries.map((q) => (
            <div
              key={q.id}
              className="border border-white/10 bg-ink/40 p-6 transition-colors duration-300 hover:border-bronze-light/40"
              data-testid="admin-inquiry-card"
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-white">
                  <User size={15} className="text-bronze-light" />
                  <span className="font-sans text-lg font-light tracking-wide">{q.name}</span>
                </div>
                <div className="flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] text-[#F1E6D7]/40">
                  <Clock size={12} />
                  {fmtDate(q.created_at)}
                </div>
              </div>
              <div className="mt-4 space-y-2 text-[14px] font-light text-[#F1E6D7]/80">
                <a href={`tel:${q.phone}`} className="flex items-center gap-2 transition-colors hover:text-white">
                  <Phone size={14} className="text-bronze-light" />
                  {q.phone}
                </a>
                <a href={`mailto:${q.email}`} className="flex items-center gap-2 transition-colors hover:text-white">
                  <Mail size={14} className="text-bronze-light" />
                  {q.email}
                </a>
              </div>
              {q.interest && (
                <p className="mt-4 text-[13px] font-light leading-relaxed text-[#F1E6D7]/60">
                  <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-bronze-light">
                    Interest&nbsp;
                  </span>
                  {q.interest}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
